import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/integrations/supabase/role-middleware";
import { buildExcerpt, ensureUniqueSlug, slugifyTitle } from "@/lib/blog-helpers";
import { optionalHttpUrlOrEmpty, publishedOnCreate, publishedOnUpdate } from "@/lib/content-form";

const blogPostFields =
  "id, slug, title, excerpt, body_markdown, share_image_url, published, published_at, created_at, updated_at";
const blogPostListFields = "id, slug, title, excerpt, published_at";

export const BLOG_PAGE_SIZE = 5;

export const listPublishedBlogPosts = createServerFn({ method: "GET" })
  .validator((d) =>
    z
      .object({
        page: z.number().int().min(0).default(0),
        pageSize: z.number().int().min(1).max(BLOG_PAGE_SIZE).default(BLOG_PAGE_SIZE),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const from = data.page * data.pageSize;
    const to = from + data.pageSize - 1;

    const {
      data: posts,
      error,
      count,
    } = await supabaseAdmin
      .from("blog_posts")
      .select(blogPostListFields, { count: "exact" })
      .eq("published", true)
      .order("published_at", { ascending: false })
      .range(from, to);

    if (error) throw new Error(error.message);

    return {
      posts: posts ?? [],
      hasMore: (count ?? 0) > to + 1,
    };
  });

export const getBlogPostBySlug = createServerFn({ method: "GET" })
  .validator((d) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: post, error } = await supabaseAdmin
      .from("blog_posts")
      .select(blogPostFields)
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post) throw new Error("Post not found");
    return post;
  });

export const listAllBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("blog_posts")
      .select(blogPostFields)
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const blogPostInput = z.object({
  title: z.string().min(1).max(200),
  body_markdown: z.string().min(1),
  excerpt: z.string().max(500).optional().nullable(),
  share_image_url: optionalHttpUrlOrEmpty,
  published: z.boolean().optional(),
  published_at: z.string().datetime().optional(),
});

export const createBlogPost = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => blogPostInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const baseSlug = slugifyTitle(data.title) || "post";
    const slug = await ensureUniqueSlug(supabase, "blog_posts", baseSlug);
    const excerpt = buildExcerpt(data.body_markdown, data.excerpt);

    const { data: post, error } = await supabase
      .from("blog_posts")
      .insert({
        slug,
        title: data.title.trim(),
        body_markdown: data.body_markdown,
        excerpt,
        share_image_url: data.share_image_url,
        ...publishedOnCreate(data),
      })
      .select(blogPostFields)
      .single();
    if (error) throw new Error(error.message);
    return post;
  });

export const updateBlogPost = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) =>
    blogPostInput
      .extend({
        id: z.string().uuid(),
        slug: z.string().min(1).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const baseSlug = data.slug ?? slugifyTitle(data.title);
    const slug = await ensureUniqueSlug(supabase, "blog_posts", baseSlug || "post", data.id);
    const excerpt = buildExcerpt(data.body_markdown, data.excerpt);

    const { data: post, error } = await supabase
      .from("blog_posts")
      .update({
        slug,
        title: data.title.trim(),
        body_markdown: data.body_markdown,
        excerpt,
        share_image_url: data.share_image_url,
        ...publishedOnUpdate(data),
      })
      .eq("id", data.id)
      .select(blogPostFields)
      .single();
    if (error) throw new Error(error.message);
    return post;
  });

export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { error } = await supabase.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
